import {
  optimizeSchedule,
  compareScores,
  buildDeterministicSeed,
  buildStaticOptimizerContext,
  findCampersWithInsufficientUniquePreferences,
  runDeepRequestedRepairOnSolution,
  validateSolutionConstraints,
} from '../solver/optimizer';

function mergeStageCounters(target, source) {
  if (!source) return target;
  Object.keys(source).forEach((k) => {
    target[k] = (target[k] || 0) + (source[k] || 0);
  });
  return target;
}

function buildSettingsSnapshot(input) {
  const settings = input.settings || {};
  return {
    groupMin: settings.groupMin ?? 10,
    groupMax: settings.groupMax ?? 25,
    oneEightRotation: settings.oneEightRotation ?? 4,
    dynamicMinDelta: settings.dynamicMinDelta ?? 7,
  };
}

function betterSolution(candidate, currentBest) {
  if (!candidate) return false;
  if (!currentBest) return true;
  return compareScores(candidate.score, currentBest.score) < 0;
}

function waitForNextTick() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export async function runWorkerSolveJob(payload, emit, shouldCancel = () => false) {
  const input = payload.input || {};
  const options = payload.options || {};
  const maxRuntimeMs = options.maxRuntimeMs ?? 120000;
  const startsPerSlice = Math.max(1, options.startsPerSlice ?? 1);
  const sliceRuntimeMs = Math.max(50, options.sliceRuntimeMs ?? 350);
  const seed = options.seed ?? buildDeterministicSeed(input);
  const staticContext = buildStaticOptimizerContext(input);
  const startedAt = Date.now();
  const control = typeof shouldCancel === 'function'
    ? { shouldStopOptimization: shouldCancel, shouldStopDeepRepair: shouldCancel }
    : shouldCancel || {};
  const shouldStopOptimization = control.shouldStopOptimization || (() => false);
  const shouldStopDeepRepair = control.shouldStopDeepRepair || (() => false);
  const shouldStartDeepRepair = control.shouldStartDeepRepair || (() => true);
  const phaseOneBudgetMs = Math.max(1, maxRuntimeMs);
  const requestCoverageWarnings = findCampersWithInsufficientUniquePreferences(input.campers || []);

  let bestSolution = null;
  let bestScore = null;
  let totalIterations = 0;
  const stageCounters = {};
  const stageTimingsMs = {};

  while ((Date.now() - startedAt) < phaseOneBudgetMs) {
    if (shouldStopOptimization()) break;
    const elapsed = Date.now() - startedAt;
    const remaining = phaseOneBudgetMs - elapsed;
    if (remaining <= 0) break;
    if (bestSolution && remaining < sliceRuntimeMs) break;
    const result = optimizeSchedule(input, {
      seed,
      startIteration: totalIterations,
      maxStarts: startsPerSlice,
      maxRuntimeMs: Math.min(sliceRuntimeMs, remaining),
      progressIntervalMs: 0,
      skipConstraintValidation: true,
      staticContext,
    });
    totalIterations += result.diagnostics.iterations || 0;
    mergeStageCounters(stageCounters, result.diagnostics.stageCounters);
    mergeStageCounters(stageTimingsMs, result.diagnostics.stageTimingsMs);

    if (betterSolution(result.solution, bestSolution)) {
      bestSolution = result.solution;
      bestScore = result.solution.score;
    }

    emit({
      type: 'progress',
      phase: 'optimization',
      iteration: totalIterations,
      elapsedMs: Date.now() - startedAt,
      bestUnplaced: bestScore ? bestScore.unplacedCampers : null,
      bestScore,
      requestCoverageWarnings,
    });

    if (bestScore && bestScore.unplacedCampers === 0) break;
    if ((result.diagnostics.iterations || 0) === 0) break;

    await waitForNextTick();
  }

  if (!bestSolution) {
    const fallback = optimizeSchedule(input, {
      seed,
      maxStarts: 1,
      maxRuntimeMs: Math.min(500, phaseOneBudgetMs),
      progressIntervalMs: 0,
      skipConstraintValidation: true,
      staticContext,
    });
    bestSolution = fallback.solution;
    bestScore = fallback.solution.score;
    totalIterations += fallback.diagnostics.iterations || 0;
    mergeStageCounters(stageCounters, fallback.diagnostics.stageCounters);
    mergeStageCounters(stageTimingsMs, fallback.diagnostics.stageTimingsMs);
  }

  let deepRepair = {
    attempted: false,
    cancelled: false,
    budgetExhausted: false,
    budgetMs: 0,
    elapsedMs: 0,
    holesAtStart: bestScore?.missingSlots || 0,
    holesResolved: 0,
    holesRemaining: bestScore?.missingSlots || 0,
    acceptedPaths: [],
    unresolvedHoles: [],
  };
  const optimizationStopped = shouldStopOptimization();
  const buildCheckpointResult = (checkpointSolution, checkpointDeepRepair) => ({
    type: 'result',
    cancelled: false,
    optimizationStopped,
    solution: checkpointSolution,
    diagnostics: {
      seed,
      iterations: totalIterations,
      elapsedMs: Date.now() - startedAt,
      bestScore: checkpointSolution?.score || bestScore,
      stageCounters,
      stageTimingsMs,
      constraintChecks: checkpointSolution ? validateSolutionConstraints(checkpointSolution, input) : {},
      demandDiagnostics: checkpointSolution?.demandDiagnostics || { skills: {}, rotations: [], phases: {} },
      deepRepair: checkpointDeepRepair,
      requestCoverageWarnings,
      settingsSnapshot: {
        ...buildSettingsSnapshot(input),
        phaseOneBudgetMs,
        phaseTwoBudgetMs: null,
        deepRepairRuntimeMode: 'until-complete-or-stopped',
      },
    },
  });
  if (bestSolution && (bestScore?.missingSlots || 0) > 0 && !shouldStopDeepRepair()) {
    deepRepair = {
      ...deepRepair,
      attempted: true,
      budgetMs: null,
    };
    emit({
      type: 'progress',
      phase: 'deep-repair',
      iteration: totalIterations,
      elapsedMs: Date.now() - startedAt,
      bestUnplaced: bestScore?.unplacedCampers ?? null,
      bestScore,
      holesAtStart: bestScore?.missingSlots || 0,
      holesRemaining: bestScore?.missingSlots || 0,
      pathsAccepted: 0,
      nodesExpanded: 0,
      requestCoverageWarnings,
      checkpointResult: buildCheckpointResult(bestSolution, deepRepair),
    });
    while (!shouldStartDeepRepair() && !shouldStopDeepRepair()) {
      await waitForNextTick();
    }
    if (!shouldStopDeepRepair()) {
      const deepResult = runDeepRequestedRepairOnSolution(input, bestSolution, {
        seed,
        maxRuntimeMs: options.deepRepairMaxRuntimeMs ?? null,
        maxDepth: options.deepRepairMaxDepth ?? 8,
        maxNodes: options.deepRepairMaxNodes ?? 200000,
        maxNodesPerHole: options.deepRepairMaxNodesPerHole ?? 25000,
        maxPasses: options.deepRepairMaxPasses,
        shouldCancel: shouldStopDeepRepair,
        staticContext,
        onProgress: (progress) => {
          const { checkpointSolution, diagnosticsSnapshot, ...progressFields } = progress;
          const checkpointResult = checkpointSolution
            ? buildCheckpointResult(checkpointSolution, diagnosticsSnapshot || deepRepair)
            : null;
          emit({
            type: 'progress',
            phase: 'deep-repair',
            iteration: totalIterations,
            elapsedMs: Date.now() - startedAt,
            bestUnplaced: bestScore?.unplacedCampers ?? null,
            bestScore,
            requestCoverageWarnings,
            ...(checkpointResult ? { checkpointResult } : {}),
            ...progressFields,
          });
        },
      });
      deepRepair = deepResult.diagnostics;
      bestSolution = deepResult.solution;
      bestScore = bestSolution.score || bestScore;
    } else {
      deepRepair.cancelled = true;
    }
  }

  const constraintChecks = bestSolution
    ? validateSolutionConstraints(bestSolution, input)
    : {};

  return {
    type: 'result',
    cancelled: !!deepRepair.cancelled || shouldStopDeepRepair(),
    optimizationStopped,
    solution: bestSolution,
    diagnostics: {
      seed,
      iterations: totalIterations,
      elapsedMs: Date.now() - startedAt,
      bestScore,
      stageCounters,
      stageTimingsMs,
      constraintChecks,
      demandDiagnostics: bestSolution?.demandDiagnostics || { skills: {}, rotations: [], phases: {} },
      deepRepair,
      requestCoverageWarnings,
      settingsSnapshot: {
        ...buildSettingsSnapshot(input),
        phaseOneBudgetMs,
        phaseTwoBudgetMs: null,
        deepRepairRuntimeMode: 'until-complete-or-stopped',
      },
    },
  };
}

const isWorkerRuntime = typeof WorkerGlobalScope !== 'undefined'
  && typeof self !== 'undefined'
  && self instanceof WorkerGlobalScope;

if (isWorkerRuntime && typeof self.postMessage === 'function') {
  let stopOptimizationRequested = false;
  let stopDeepRepairRequested = false;
  let deepRepairStartRequested = false;
  let activeToken = 0;

  self.onmessage = (event) => {
    const msg = event.data || {};
    if (msg.type === 'stop-optimization') {
      stopOptimizationRequested = true;
      return;
    }
    if (msg.type === 'stop-deep-repair') {
      stopDeepRepairRequested = true;
      return;
    }
    if (msg.type === 'begin-deep-repair') {
      deepRepairStartRequested = true;
      return;
    }
    if (msg.type === 'cancel') {
      stopOptimizationRequested = true;
      stopDeepRepairRequested = true;
      return;
    }
    if (msg.type !== 'start') return;

    stopOptimizationRequested = false;
    stopDeepRepairRequested = false;
    deepRepairStartRequested = false;
    const token = ++activeToken;
    const emit = (payload) => {
      if (token !== activeToken) return;
      self.postMessage(payload);
    };
    runWorkerSolveJob(
      msg.payload || {},
      emit,
      {
        shouldStopOptimization: () => stopOptimizationRequested || token !== activeToken,
        shouldStopDeepRepair: () => stopDeepRepairRequested || token !== activeToken,
        shouldStartDeepRepair: () => deepRepairStartRequested || token !== activeToken,
      }
    )
      .then((resultPayload) => {
        emit(resultPayload);
      })
      .catch((err) => {
        emit({
          type: 'error',
          message: err && err.message ? err.message : 'Unknown scheduler worker failure',
        });
      });
  };
}
